<template>
  <figure style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
    <chart :options="themeRiverOptions" ref="themeRiver" auto-resize style='width: 100%' v-if="dataState === 'ready'"></chart>
    <template v-else-if="dataState === 'loading'">
      <center>
        <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </center>
    </template>
    <template v-else-if="dataState === 'noData'">
      <div class="d-flex" style="flex-direction: column; align-items: center"><v-icon class="primary--text" style="font-size: 40px !important;width:100%">warning</v-icon><p class="accent--text mb-0 ml-2 subheading font-weight-bold">no data found</p></div>
    </template>
    <template v-else-if="dataState === 'error'">
      <div class="d-flex" style="flex-direction: column; align-items: center"><v-icon class="error--text" style="font-size: 40px !important">warning</v-icon><p class="accent--text mb-0 ml-2 subheading font-weight-bold">error connecting to server</p></div>
    </template>
  </figure>
</template>

<script>
import axios from 'axios'
import underscore from 'underscore'
import { formatEchartsDateTime, formatEchartsDate, formatEchartsMonth } from '@/helpers/formatters'
import { mapGetters, mapMutations } from 'vuex'
import 'echarts/lib/chart/themeRiver'

export default {
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      dataState: 'loading',
      timeInterval: 'day',
      hourly: false,
      themeRiverOptions: {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(0,0,0,0.2)',
              width: 1,
              type: 'solid'
            }
          }
        },

        legend: {
          data: []
        },

        singleAxis: {
          top: 50,
          bottom: 50,
          axisTick: {},
          axisLabel: {},
          type: 'time',
          axisPointer: {
            animation: true,
            label: {
              show: true
            }
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              opacity: 0.2
            }
          }
        },

        series: [
          {
            label: { show: false },
            type: 'themeRiver',
            itemStyle: {
              emphasis: {
                shadowBlur: 20,
                shadowColor: 'rgba(0, 0, 0, 0.8)'
              }
            },
            data: []
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('filters', ['getQuery', 'getQueryExcluding'])
  },
  methods: {
    ...mapMutations('notification', ['notify']),
    ...mapMutations('filters', ['setCustomFilters']),
    getMonth: function (month) {
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ]
      return months[month]
    },
    getTopEntities: function (nbEntities) {
      this.dataState = 'loading'
      axios.all([
        this.$store.dispatch('getTweetsEntities', this.getQuery({pageSize: nbEntities, sortBy: '_tweet_count', order: 'DESC'}))
      ]).then(axios.spread((r1) => {
        if (r1.data.status === 'SUCCESS') {
          if (r1.data.payload.length === 0) {
            this.dataState = 'noData'
          } else {
            let topEntities = []
            for (const i in r1.data.payload) {
              topEntities.push(r1.data.payload[i]._name)
            }
            this.getData(topEntities)
          }
        } else {
          this.dataState = 'error'
          this.notify({message: 'Something went wrong while fetching data.', show: true})
        }
      })).catch((err) => {
        this.dataState = 'error'
        this.notify({message: 'Something went wrong while connecting to the server.', show: true})
        console.log(err)
      })
    },
    getData: function (topEntities) {
      var toDay = 24 * 60 * 60 * 1000
      var timeInterval = (new Date(this.filters.toDate) - new Date(this.filters.fromDate)) / toDay
      if (Array.isArray(this.filters.toTime) || Array.isArray(this.filters.fromTime)) {
        this.hourly = false
      } else {
        this.hourly = true
      }
      if (this.hourly) {
        this.timeInterval = 'minute'
      } else if (timeInterval <= 7) {
        this.timeInterval = 'hour'
      } else if (timeInterval <= 91) {
        this.timeInterval = 'day'
      } else if (timeInterval <= 183) {
        this.timeInterval = 'wom'
      } else {
        this.timeInterval = 'month'
      }
      this.dataState = 'loading'
      axios.all([
        this.$store.dispatch('getTweetsEntitiesVolume', this.getQueryExcluding({exclude: 'entity', entity: topEntities, excludeRelatedEntities: true, interval: this.timeInterval}))
      ]).then(axios.spread((r1) => {
        if (r1.data.status === 'SUCCESS') {
          let data = []
          let seriesData = {}
          let i = 0
          let date = {}

          if (r1.data.payload.length === 0) {
            seriesData = [{value: 0, name: 'None'}]
            this.dataState = 'noData'
          } else {
            if (r1.data.payload[0].hasOwnProperty('_created_at_minute')) {
              var startTime = new Date(this.filters.fromDate + ' ' + this.filters.fromTime)
              var endTime = new Date(this.filters.toDate + ' ' + this.filters.toTime)
              while (startTime <= endTime) {
                date[formatEchartsDateTime(startTime)] = 0
                startTime = new Date(startTime.setMinutes(startTime.getMinutes() + 1))
              }
              this.themeRiverOptions.singleAxis.data = Object.keys(date)
              for (let i = 0; i < topEntities.length; i++) {
                seriesData[topEntities[i]] = JSON.parse(JSON.stringify(date))
              }
              for (i = 0; i < r1.data.payload.length; i++) {
                date = formatEchartsDateTime(new Date(Date.UTC(r1.data.payload[i]._created_at_year, r1.data.payload[i]._created_at_month - 1, r1.data.payload[i]._created_at_day, r1.data.payload[i]._created_at_hour, r1.data.payload[i]._created_at_minute)))
                seriesData[r1.data.payload[i]._name][date] = r1.data.payload[i]._tweet_count
              }
              for (const name in seriesData) {
                for (const time in seriesData[name]) {
                  data.push([time, seriesData[name][time], name])
                }
              }
            } else if (r1.data.payload[0].hasOwnProperty('_created_at_hour')) {
              var hour = new Date(this.filters.fromDate)
              var hourEnd = new Date(this.filters.toDate)

              hour.setHours(0)
              hour.setMinutes(0)
              hour.setSeconds(0)
              hourEnd.setHours(23)
              hourEnd.setMinutes(59)
              hourEnd.setSeconds(59)
              while (hour <= hourEnd) {
                date[formatEchartsDateTime(hour)] = 0
                hour = new Date(hour.setHours(hour.getHours() + 1))
              }
              this.themeRiverOptions.singleAxis.data = Object.keys(date)
              for (let i = 0; i < topEntities.length; i++) {
                seriesData[topEntities[i]] = JSON.parse(JSON.stringify(date))
              }

              for (i = 0; i < r1.data.payload.length; i++) {
                date = formatEchartsDateTime(new Date(Date.UTC(r1.data.payload[i]._created_at_year, r1.data.payload[i]._created_at_month - 1, r1.data.payload[i]._created_at_day, r1.data.payload[i]._created_at_hour)))
                seriesData[r1.data.payload[i]._name][date] = r1.data.payload[i]._tweet_count
              }

              for (const name in seriesData) {
                for (const hour in seriesData[name]) {
                  data.push([hour, seriesData[name][hour], name])
                }
              }
            } else if (r1.data.payload[0].hasOwnProperty('_created_at_day') && !r1.data.payload[0].hasOwnProperty('_created_at_wom')) {
              var day = new Date(this.filters.fromDate)
              var dayEnd = new Date(this.filters.toDate)

              dayEnd.setHours(23)
              dayEnd.setMinutes(59)
              dayEnd.setSeconds(59)

              while (day <= dayEnd) {
                date[formatEchartsDate(day)] = 0
                day = new Date(day.setDate(day.getDate() + 1))
              }

              for (let i = 0; i < topEntities.length; i++) {
                seriesData[topEntities[i]] = JSON.parse(JSON.stringify(date))
              }

              for (i = 0; i < r1.data.payload.length; i++) {
                date = formatEchartsDate(new Date(r1.data.payload[i]._created_at_year, r1.data.payload[i]._created_at_month - 1, r1.data.payload[i]._created_at_day))
                seriesData[r1.data.payload[i]._name][date] = r1.data.payload[i]._tweet_count
              }

              for (const name in seriesData) {
                for (const date in seriesData[name]) {
                  data.push([date, seriesData[name][date], name])
                }
              }
            } else if (r1.data.payload[0].hasOwnProperty('_created_at_wom')) {
              var week = new Date(this.filters.fromDate)
              var weekEnd = new Date(this.filters.toDate)

              weekEnd.setHours(23)
              weekEnd.setMinutes(59)
              weekEnd.setSeconds(59)

              while (week <= weekEnd) {
                date[formatEchartsDate(week)] = 0
                week = new Date(week.setDate(week.getDate() + 7))
              }
              for (let i = 0; i < topEntities.length; i++) {
                seriesData[topEntities[i]] = JSON.parse(JSON.stringify(date))
              }
              week = new Date(this.filters.fromDate)
              let oneDay = 24 * 60 * 60 * 1000
              for (i = 0; i < r1.data.payload.length; i++) {
                date = new Date(r1.data.payload[i]._created_at_year, r1.data.payload[i]._created_at_month - 1, r1.data.payload[i]._created_at_day, 0)
                let diffDays = Math.round(Math.abs((date.getTime() - week.getTime()) / (oneDay)))
                let woy = new Date(this.filters.fromDate)
                woy.setDate(week.getDate() + (diffDays - diffDays % 7))
                seriesData[r1.data.payload[i]._name][formatEchartsDate(woy)] += r1.data.payload[i]._tweet_count
              }
              for (const name in seriesData) {
                for (const week in seriesData[name]) {
                  data.push([week, seriesData[name][week], name])
                }
              }
            } else {
              var monthStart = new Date(this.filters.fromDate)
              var monthEnd = new Date(this.filters.toDate)
              var refDay = monthEnd.getDate()
              var yearMonthly = monthStart.getFullYear()

              while (monthStart <= monthEnd) {
                var monthOfYear = monthStart.getMonth()
                if (monthOfYear === 0) {
                  yearMonthly = monthStart.getFullYear()
                }
                date[formatEchartsMonth(monthStart)] = 0
                monthStart = new Date(yearMonthly, monthOfYear + 1, refDay)
              }

              for (let i = 0; i < topEntities.length; i++) {
                seriesData[topEntities[i]] = JSON.parse(JSON.stringify(date))
              }

              for (i = 0; i < r1.data.payload.length; i++) {
                var monthFormatted = formatEchartsMonth(new Date(r1.data.payload[i]._created_at_year, r1.data.payload[i]._created_at_month))
                seriesData[r1.data.payload[i]._name][monthFormatted] = r1.data.payload[i]._tweet_count
              }

              for (const name in seriesData) {
                for (const month in seriesData[name]) {
                  data.push([month, seriesData[name][month], name])
                }
              }
            }
            this.themeRiverOptions.series[0].data = data
            this.themeRiverOptions.legend.data = topEntities
            this.dataState = 'ready'
          }
        } else {
          this.dataState = 'error'
          this.notify({message: 'Something went wrong while fetching data.', show: true})
        }
      })).catch((err) => {
        this.dataState = 'error'
        this.notify({message: 'Something went wrong while connecting to the server.', show: true})
        console.log(err)
      })
    }
  },
  watch: {
    filters: {
      deep: true,
      immediate: true,
      handler (newVal, oldVal) {
        if (!underscore.isEqual(newVal, oldVal) && this.filters.dirty === false) {
          this.getTopEntities(5)
        }
      }
    },
    loading: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal === true) {
          let themeRiver = this.$refs.themeRiver
          if (themeRiver) {
            themeRiver.showLoading({
              text: '',
              color: '#4ea397',
              maskColor: 'rgba(255, 255, 255, 0.4)'
            })
          }
        } else {
          let themeRiver = this.$refs.themeRiver
          if (themeRiver) {
            themeRiver.hideLoading()
          }
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/charts/EntityTweetsRiverChart.vue